import { Injectable } from '@angular/core';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { VehicleService } from '@polaris-customerweb/sharedcomponents.garage';
import {
    ConnectedVehicleService,
    VehiclePermissionService,
} from '@polaris-customerweb/sharedcomponents.vehicle-health';
import { of } from 'rxjs';
import {
    catchError,
    concatMap,
    debounceTime,
    filter,
    map,
    mergeMap,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { AppState } from 'src/app/models/app-state';
import * as VehicleLocationActions from './vehicle-location.actions';

@Injectable()
export class VehicleLocationEffects {
    loadVehicleLocation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(VehicleLocationActions.GetVehicleLocation),
            mergeMap(({ vin }) =>
                this.connectedVehicleService.getVehicleLocations(vin).pipe(
                    map((locationResponse) =>
                        VehicleLocationActions.GetVehicleLocationSuccess({
                            locationResponse,
                        })
                    ),
                    catchError((errorMessage) =>
                        of(
                            VehicleLocationActions.GetVehicleLocationFail({
                                errorMessage,
                                vin,
                            })
                        )
                    )
                )
            )
        );
    });

    loadAllVehicleLocations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(VehicleLocationActions.GetAllVehicleLocations),
            withLatestFrom(
                this.vehiclePermissionService.vinsWithLocationPermission()
            ),
            filter(([, vins]) => vins.length > 0),
            concatMap(([, vins]) => {
                return this.connectedVehicleService
                    .getMultiVehicleLocation(vins)
                    .pipe(
                        switchMap((multiLocationResponse) => {
                            const actions: Action[] = [];
                            if (
                                multiLocationResponse.responses?.some(
                                    (response) =>
                                        response.status !==
                                        'vehicleNotConnected'
                                )
                            ) {
                                actions.push(
                                    VehicleLocationActions.GetAllVehicleLocationsSuccess(
                                        {
                                            locationResponses:
                                                multiLocationResponse.responses,
                                        }
                                    )
                                );
                            } else {
                                actions.push(
                                    VehicleLocationActions.GetAllVehicleLocationsFail(
                                        {
                                            errorMessage:
                                                'No locations found for vehicles',
                                            vins,
                                        }
                                    )
                                );
                            }

                            return actions;
                        }),
                        catchError((errorMessage) => {
                            return [
                                VehicleLocationActions.GetAllVehicleLocationsFail(
                                    {
                                        errorMessage,
                                        vins,
                                    }
                                ),
                            ];
                        })
                    );
            })
        );
    });

    pollForVehicleLocation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(VehicleLocationActions.GetVehicleLocationSuccess),
            debounceTime(this.configService.pollTimers.location * 1000),
            withLatestFrom(this.store),
            filter(([{ locationResponse }, state]) => {
                return (
                    state.vehicleLocation.isPollingActiveVehicle &&
                    this.vehicleService.$activeVehicle()?.vin ===
                        locationResponse.vin
                );
            }),
            mergeMap(([{ locationResponse }]) =>
                of(
                    VehicleLocationActions.GetVehicleLocation({
                        vin: locationResponse.vin || '',
                    })
                )
            )
        );
    });

    pollForAllVehicleLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleLocationActions.GetAllVehicleLocationsSuccess),
            debounceTime(this.configService.pollTimers.location * 1000),
            withLatestFrom(this.store),
            filter(([, state]) => state.vehicleLocation.isPollingAllVehicles),
            mergeMap(() => of(VehicleLocationActions.GetAllVehicleLocations()))
        )
    );

    getVehicleLocationOnPoll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleLocationActions.SetIsPollingActiveVehicleLocation),
            filter(({ isPolling }) => isPolling),
            switchMap(() => {
                return of(
                    VehicleLocationActions.GetVehicleLocation({
                        vin: this.vehicleService.$activeVehicle()?.vin || '',
                    })
                );
            })
        )
    );

    getAllVehicleLocationOnPoll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VehicleLocationActions.SetIsPollingAllVehicleLocations),
            filter(({ isPolling }) => isPolling),
            switchMap(() => {
                return of(VehicleLocationActions.GetAllVehicleLocations());
            })
        )
    );

    constructor(
        private readonly actions$: Actions,
        private readonly configService: ConfigService,
        private readonly connectedVehicleService: ConnectedVehicleService,
        private readonly store: Store<AppState>,
        private readonly vehiclePermissionService: VehiclePermissionService,
        private readonly vehicleService: VehicleService
    ) {}
}

export const vehicleLocationEffectsModule = EffectsModule.forFeature([
    VehicleLocationEffects,
]);
